import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft,
  Users2,
  MapPin,
  Crown,
  Activity,
  TrendingUp,
  Calendar,
  User,
  Mail,
  Phone,
  Target,
  Award,
  Zap,
  BarChart3,
  Star,
  Loader
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart as RechartsPieChart, Cell, AreaChart, Area, Pie } from 'recharts';

const GroupDetailsPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [groupDetails, setGroupDetails] = useState(null);
  const [members, setMembers] = useState([]);
  const [groupStats, setGroupStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchGroupDetails();
    fetchGroupMembers();
    fetchGroupStats();
  }, [groupId]);

  const fetchGroupDetails = async () => {
    try {
      const response = await axios.get(`/groups/${groupId}`);
      setGroupDetails(response.data);
    } catch (error) {
      console.error('Error fetching group details:', error);
      setError('Failed to load group details');
    }
  };

  const fetchGroupMembers = async () => {
    try {
      const response = await axios.get(`/groups/${groupId}/members`);
      if (response.data && Array.isArray(response.data)) {
        setMembers(response.data);
      }
    } catch (error) {
      console.error('Error fetching group members:', error);
      setMembers([]);
    }
  };

  const fetchGroupStats = async () => {
    try {
      const response = await axios.get(`/groups/${groupId}/stats`);
      setGroupStats(response.data);
    } catch (error) {
      console.error('Error fetching group stats:', error);
      setGroupStats(null);
    } finally {
      setLoading(false);
    }
  };

  // Generate sample data for charts - in production, this would come from the API
  const generateWeeklyTrends = () => {
    // Use real data if available, otherwise generate sample data
    if (groupStats && groupStats.weeklyTrends && groupStats.weeklyTrends.length > 0) {
      return groupStats.weeklyTrends.map(day => ({
        day: day.day,
        steps: parseInt(day.total_steps) || 0,
        distance: parseFloat(day.total_distance) || 0,
        calories: parseInt(day.total_calories) || 0,
        activeMembers: parseInt(day.active_members) || 0
      }));
    }
    
    // Fallback to sample data for demonstration
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map((day, index) => ({
      day,
      steps: Math.floor(Math.random() * 50000) + 30000,
      distance: Math.floor(Math.random() * 30) + 20,
      calories: Math.floor(Math.random() * 3000) + 2000,
      activeMembers: Math.floor(Math.random() * (members.length * 0.3)) + Math.floor(members.length * 0.6)
    }));
  };

  const generateMemberPerformance = () => {
    // Use real data if available
    if (groupStats && groupStats.memberPerformance && groupStats.memberPerformance.length > 0) {
      return groupStats.memberPerformance.map(member => ({
        name: (member.name || `${member.first_name} ${member.last_name}`).split(' ')[0],
        steps: parseInt(member.total_steps) || 0,
        distance: parseFloat(member.total_distance) || 0,
        calories: parseInt(member.total_calories) || 0,
        activities: parseInt(member.activity_count) || 0
      }));
    }
    
    // Fallback to member data with sample stats
    return members.slice(0, 10).map(member => ({
      name: (member.name || `${member.first_name} ${member.last_name}`).split(' ')[0],
      steps: member.total_steps || Math.floor(Math.random() * 40000) + 10000,
      distance: member.total_distance || Math.floor(Math.random() * 25) + 5,
      calories: member.total_calories || Math.floor(Math.random() * 2500) + 500,
      activities: member.activity_count || Math.floor(Math.random() * 15) + 5
    }));
  };

  const generateActivityDistribution = () => {
    // Use real data if available
    if (groupStats && groupStats.activityDistribution && groupStats.activityDistribution.length > 0) {
      const colors = [chartColors.primary, chartColors.secondary, chartColors.accent, chartColors.danger, chartColors.purple];
      return groupStats.activityDistribution.map((activity, index) => ({
        name: activity.activity_type || 'Unknown',
        value: parseFloat(activity.percentage) || 0,
        color: colors[index % colors.length]
      }));
    }
    
    // Fallback to sample data
    const activityTypes = ['Walking', 'Running', 'Cycling', 'Hiking', 'Gym'];
    const colors = [chartColors.primary, chartColors.secondary, chartColors.accent, chartColors.danger, chartColors.purple];
    return activityTypes.map((type, index) => ({
      name: type,
      value: Math.floor(Math.random() * 30) + 10,
      color: colors[index]
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const viewUserStats = (userId) => {
    navigate(`/users/${userId}/stats`);
  };

  // Chart colors
  const chartColors = {
    primary: '#3B82F6',
    secondary: '#10B981',
    accent: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6',
    pink: '#EC4899'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-blue-600 mx-auto" />
          <p className="text-gray-600 mt-4">Loading group details...</p>
        </div>
      </div>
    );
  }

  if (error || !groupDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Group</h2>
          <p className="text-gray-600 mb-6">{error || 'Group not found'}</p>
          <button
            onClick={() => navigate('/groups')}
            className="btn-primary flex items-center mx-auto"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Groups
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/groups')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-6 w-6 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{groupDetails.name}</h1>
                <p className="text-gray-600">Complete Group Analytics & Member Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <MapPin className="h-4 w-4" />
                <span>{groupDetails.city}</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Users2 className="h-4 w-4" />
                <span>{members.length} members</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Total Steps</p>
                  <p className="text-3xl font-bold">{(groupDetails.total_steps || 0).toLocaleString()}</p>
                </div>
                <Activity className="h-12 w-12 text-blue-200" />
              </div>
              <div className="mt-4 flex items-center">
                <TrendingUp className="h-4 w-4 text-blue-200 mr-1" />
                <span className="text-blue-100 text-sm">Last 7 days</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Distance</p>
                  <p className="text-3xl font-bold">{(groupDetails.total_distance || 0).toFixed(1)} km</p>
                </div>
                <MapPin className="h-12 w-12 text-green-200" />
              </div>
              <div className="mt-4 flex items-center">
                <Star className="h-4 w-4 text-green-200 mr-1" />
                <span className="text-green-100 text-sm">Group total</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm font-medium">Calories</p>
                  <p className="text-3xl font-bold">{(groupDetails.total_calories || 0).toLocaleString()}</p>
                </div>
                <Zap className="h-12 w-12 text-orange-200" />
              </div>
              <div className="mt-4 flex items-center">
                <Award className="h-4 w-4 text-orange-200 mr-1" />
                <span className="text-orange-100 text-sm">Calories burned</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm font-medium">Members</p>
                  <p className="text-3xl font-bold">{members.length}</p>
                </div>
                <Users2 className="h-12 w-12 text-purple-200" />
              </div>
              <div className="mt-4 flex items-center">
                <Crown className="h-4 w-4 text-purple-200 mr-1" />
                <span className="text-purple-100 text-sm">Active members</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'overview'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('members')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'members'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Members ({members.length})
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'analytics'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Analytics
                </button>
              </nav>
            </div>

            <div className="p-6">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Group Information */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Group Information</h3>
                      <div className="space-y-4">
                        <div className="flex items-center space-x-3">
                          <Users2 className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Group Name</p>
                            <p className="font-medium">{groupDetails.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <MapPin className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Location</p>
                            <p className="font-medium">{groupDetails.city}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Calendar className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-500">Created</p>
                            <p className="font-medium">{formatDate(groupDetails.created_at)}</p>
                          </div>
                        </div>
                        {groupDetails.hot_stepper_name && (
                          <div className="flex items-center space-x-3">
                            <Crown className="h-5 w-5 text-yellow-500" />
                            <div>
                              <p className="text-sm text-gray-500">Hot Stepper</p>
                              <p className="font-medium">{groupDetails.hot_stepper_name}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Metrics</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {Math.round((groupDetails.total_steps || 0) / Math.max(members.length, 1)).toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">Avg Steps per Member</div>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {((groupDetails.total_distance || 0) / Math.max(members.length, 1)).toFixed(1)} km
                          </div>
                          <div className="text-sm text-gray-600 mt-1">Avg Distance per Member</div>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg">
                          <div className="text-2xl font-bold text-orange-600">
                            {Math.round((groupDetails.total_calories || 0) / Math.max(members.length, 1)).toLocaleString()}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">Avg Calories per Member</div>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg">
                          <div className="text-2xl font-bold text-purple-600">
                            {members.filter(m => m.membership_type === 'premium').length}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">Premium Members</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'members' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Group Members</h3>
                  {members.length === 0 ? (
                    <div className="text-center py-12">
                      <Users2 className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No members found</h3>
                      <p className="mt-1 text-sm text-gray-500">This group doesn't have any members yet.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {members.map((member) => (
                        <div 
                          key={member.id} 
                          className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => viewUserStats(member.id)}
                        >
                          <div className="flex items-center space-x-4 mb-4">
                            <div className="h-12 w-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-medium text-lg">
                                {(member.name || member.first_name || 'U').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-gray-900">
                                  {member.name || `${member.first_name} ${member.last_name}`}
                                </h4>
                                {member.membership_type === 'premium' && (
                                  <Crown className="h-4 w-4 text-yellow-500" />
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{member.email}</p>
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Steps:</span>
                              <span className="font-medium">{(member.total_steps || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Distance:</span>
                              <span className="font-medium">{(member.total_distance || 0).toFixed(1)} km</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Activities:</span>
                              <span className="font-medium">{member.activity_count || 0}</span>
                            </div>
                          </div>

                          <button className="w-full mt-4 btn-secondary text-sm flex items-center justify-center">
                            <BarChart3 className="h-4 w-4 mr-2" />
                            View Stats
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'analytics' && (
                <div className="space-y-8">
                  <h3 className="text-lg font-semibold text-gray-900">Group Analytics</h3>
                  
                  {/* Weekly Activity Trends */}
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">Weekly Activity Trends</h4>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={generateWeeklyTrends()}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis 
                              dataKey="day" 
                              stroke="#6b7280"
                              fontSize={12}
                            />
                            <YAxis stroke="#6b7280" fontSize={12} />
                            <Tooltip 
                              formatter={(value, name) => [
                                name === 'steps' ? value.toLocaleString() : 
                                name === 'distance' ? `${value} km` :
                                name === 'calories' ? `${value} cal` :
                                `${value} members`,
                                name === 'steps' ? 'Steps' :
                                name === 'distance' ? 'Distance' :
                                name === 'calories' ? 'Calories' :
                                'Active Members'
                              ]}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="steps" 
                              stroke={chartColors.primary}
                              strokeWidth={2}
                              dot={{ fill: chartColors.primary, strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6 }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="activeMembers" 
                              stroke={chartColors.secondary}
                              strokeWidth={2}
                              dot={{ fill: chartColors.secondary, strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Activity Distribution */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-md font-semibold text-gray-900 mb-4">Activity Distribution</h4>
                      <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                          <RechartsPieChart>
                            <Pie
                              data={generateActivityDistribution()}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            >
                              {generateActivityDistribution().map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value}%`, 'Activity Share']} />
                          </RechartsPieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Member Performance Comparison */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-6">Top Member Performance (Last 30 Days)</h4>
                    <div className="h-96">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={generateMemberPerformance()} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="name" 
                            stroke="#6b7280"
                            fontSize={12}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis stroke="#6b7280" fontSize={12} />
                          <Tooltip 
                            formatter={(value, name) => [
                              name === 'steps' ? value.toLocaleString() : 
                              name === 'distance' ? `${value} km` :
                              name === 'calories' ? `${value} cal` :
                              `${value} activities`,
                              name === 'steps' ? 'Steps' :
                              name === 'distance' ? 'Distance' :
                              name === 'calories' ? 'Calories' :
                              'Activities'
                            ]}
                          />
                          <Bar dataKey="steps" fill={chartColors.primary} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Weekly Metrics Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-blue-800">Weekly Steps</h5>
                        <Activity className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="text-2xl font-bold text-blue-900">
                        {generateWeeklyTrends().reduce((sum, day) => sum + day.steps, 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-blue-600 mt-1">
                        +12% from last week
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-green-800">Weekly Distance</h5>
                        <MapPin className="h-5 w-5 text-green-600" />
                      </div>
                      <div className="text-2xl font-bold text-green-900">
                        {generateWeeklyTrends().reduce((sum, day) => sum + day.distance, 0).toFixed(1)} km
                      </div>
                      <div className="text-sm text-green-600 mt-1">
                        +8% from last week
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-orange-800">Weekly Calories</h5>
                        <Zap className="h-5 w-5 text-orange-600" />
                      </div>
                      <div className="text-2xl font-bold text-orange-900">
                        {generateWeeklyTrends().reduce((sum, day) => sum + day.calories, 0).toLocaleString()}
                      </div>
                      <div className="text-sm text-orange-600 mt-1">
                        +15% from last week
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium text-purple-800">Avg Active Members</h5>
                        <Users2 className="h-5 w-5 text-purple-600" />
                      </div>
                      <div className="text-2xl font-bold text-purple-900">
                        {Math.round(generateWeeklyTrends().reduce((sum, day) => sum + day.activeMembers, 0) / 7)}
                      </div>
                      <div className="text-sm text-purple-600 mt-1">
                        {Math.round((generateWeeklyTrends().reduce((sum, day) => sum + day.activeMembers, 0) / 7 / members.length) * 100)}% of total
                      </div>
                    </div>
                  </div>

                  {/* Member Activity Comparison */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-6">Member Distance Comparison</h4>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={generateMemberPerformance()}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="name" 
                            stroke="#6b7280"
                            fontSize={12}
                          />
                          <YAxis stroke="#6b7280" fontSize={12} />
                          <Tooltip 
                            formatter={(value, name) => [`${value} km`, 'Distance']}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="distance" 
                            stroke={chartColors.accent}
                            fill={chartColors.accent}
                            fillOpacity={0.3}
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupDetailsPage; 